import { useEffect, useMemo, useState } from 'react'
import { icons, logos } from 'common'
import { CopyIcon, PrivatePresaleCSS } from './PrivatePresaleCSS.styled'
import { ContentSwitcherByState } from 'components/Atoms/ContentSwitcherByState'
import { Helmet } from 'react-helmet'
import { currencyFormatter, textMiddleTruncate } from 'utils'

import { utils } from 'ethers'
import { useEthers, useEtherBalance, useContractFunction, Mainnet, Sepolia, useCall } from '@usedapp/core'
import { formatEther, formatUnits } from '@ethersproject/units'
import { Zero } from '@ethersproject/constants'
import { tokenSaleContract, tokenSaleAddress } from 'constants/contracts/tokenSale'
import { SingleDataLoader } from 'components/Atoms/SingleDataLoader'
import { LONG_DASH } from 'constants'
import { doCopy } from 'utils'
import { Link } from 'components/Atoms/Link'
import { useNavigate, useParams } from 'react-router-dom'
import { useAPI } from 'utils/hooks/useAPI'
import { API } from 'constants/apiEndPoints'
import { onlyDigits } from 'utils/uiUtils_TS'

const NETWORK = Mainnet //Sepolia //Mainnet
const YOLO_TOKEN_USD = 0.04
const MIN_USD_CONTRIBUTION = 1000
// const YOLO_TOKEN_DECIMALS = 18

const VALID_INFLUENCERS = ['wiz', 'defi_brian', 'aditya', 'mike', 'hoss']

const useEthPrice = () => {
  const CONTRACT_ETH_DECIMALS = 8
  const { value: ethPriceArrayBN = [Zero] } =
    useCall({
      contract: tokenSaleContract,
      method: 'getLatestEthPrice',
    }) ?? {}

  const ethPrice = useMemo(() => {
    const ethPriceBN = ethPriceArrayBN.at(0)
    const ethPrice = formatUnits(ethPriceBN, CONTRACT_ETH_DECIMALS)
    return Number(ethPrice)
  }, [ethPriceArrayBN])

  return ethPrice
}

export const PrivatePresale = () => {
  const { influencer } = useParams()
  const navigateTo = useNavigate()
  const [registerTx, setRegisterTx] = useState(false)

  const [stateTxInfo, sendTxInfo, hasTxInfoStatus] = useAPI(API.YOLO_TOKEN_PRIVATE_PRESALE, {
    controlled: true,
    withJwt: true,
  })

  useEffect(() => {
    const isValidInfluencer = VALID_INFLUENCERS.includes(influencer)
    setRegisterTx(isValidInfluencer)
    if (influencer && !isValidInfluencer) navigateTo('/game/private_presale/9a4c6a5f-f1e5-42be-a583-4131c9e5d65c')
  }, [influencer, navigateTo])

  const openDocsInNewTab = () => {
    const url = 'https://yolorekt.finance/game/yolo_token'
    const newWindow = window.open(url, '_blank')
    newWindow.focus()
  }

  //Web3 code section
  const web3 = useEthers()
  const etherPrice = useEthPrice()
  const etherBalance = useEtherBalance(web3.account)

  const {
    state: tokenPurchaseState,
    send: buyToken,
    resetState: tokenPurchaseResetStatus,
  } = useContractFunction(tokenSaleContract, 'buy', {
    gasLimitBufferPercentage: 10,
    transactionName: 'buyToken',
    chainId: web3.chainId,
  })

  const { value: purchasedAmount } =
    useCall({
      contract: tokenSaleContract,
      method: 'purchasedAmount',
      args: [web3.account],
    }) ?? {}

  const [connectionState, setConnectionState] = useState('notConnected')
  const [isMainnet, setIsMainnet] = useState(true)

  useEffect(() => {
    if (web3?.account) setConnectionState('connected')
    else setConnectionState('notConnected')
    if (web3?.chainId === NETWORK.chainId) setIsMainnet(true)
    else setIsMainnet(false)
  }, [web3?.account, web3?.chainId])

  const ethBalanceInFiat = useMemo(() => {
    const ethBalance = formatEther(etherBalance || 0)
    return ethBalance * etherPrice
  }, [etherPrice, etherBalance])

  const yoloTokenPurchasedBalance = useMemo(() => {
    const yoloTokenAmount = formatEther(purchasedAmount?.at(0) || Zero)
    return yoloTokenAmount
  }, [purchasedAmount])

  const onMmConnect = (e) => {
    e.preventDefault()
    web3.activateBrowserWallet()
  }

  const onMmDisconnect = (e) => {
    e.preventDefault()
    web3.deactivate()
  }
  const onSwitchToNetwork = (e) => {
    e.preventDefault()
    web3.switchNetwork(NETWORK.chainId)
  }

  // Web3 code section

  // calculator section
  const [ethFiatAmount, setEthFiatAmount] = useState()
  const [ethAmount, setEthAmount] = useState('')
  const [error, setError] = useState()
  const [loading, setLoading] = useState(false)

  const coontributeButtonDisabled = useMemo(
    () => !(web3?.account && ethAmount && ethFiatAmount > MIN_USD_CONTRIBUTION),
    [web3?.account, ethAmount, ethFiatAmount]
  )

  const onEthChange = (e) => {
    setError('')
    const inputValue = e.target.value
    const ethAmount = onlyDigits(inputValue)
    const ethFiatAmount = Number((ethAmount * etherPrice).toFixed(2))
    if (inputValue && ethFiatAmount < MIN_USD_CONTRIBUTION) {
      setError(
        `Your contribution does not meet the ${currencyFormatter(MIN_USD_CONTRIBUTION, {
          decimalDigits: 0,
        })} USD minimum`
      )
    }
    setEthFiatAmount(ethFiatAmount)
    setEthAmount(ethAmount || '')
  }

  const buyTokens = (e) => {
    e.preventDefault()
    setError('')
    buyToken({ value: utils.parseEther(ethAmount.toString()) })
  }

  const onContributeAgain = (e) => {
    e.preventDefault()
    tokenPurchaseResetStatus()
  }

  useEffect(() => {
    switch (tokenPurchaseState?.status) {
      case 'None':
        break
      case 'PendingSignature':
      case 'Mining':
        setLoading(true)
        break
      case 'Success':
        if (registerTx) {
          const payload = {
            referralName: influencer,
            userAddress: tokenPurchaseState?.receipt?.from,
            transactionHash: tokenPurchaseState?.receipt?.transactionHash,
            status: 'success',
          }
          sendTxInfo({ params: payload })
        }
        setLoading(false)
        break
      case 'Fail':
      case 'Exception':
        if (tokenPurchaseState.errorMessage === 'execution reverted') {
          setError(
            `Your contribution does not meet the ${currencyFormatter(MIN_USD_CONTRIBUTION, {
              decimalDigits: 0,
            })} USD minimum`
          )
        } else setError(tokenPurchaseState.errorMessage)
        setLoading(false)
        break
      case 'CollectingSignaturePool':
        break

      default:
        break
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tokenPurchaseState, influencer, registerTx])
  // calculator section

  //copy to clipboard
  const [hasCopied, setHasCopied] = useState(false)
  useEffect(() => {
    if (hasCopied) {
      const timer = setTimeout(() => {
        setHasCopied(false)
      }, 2000)
      return () => clearTimeout(timer)
    }
  }, [hasCopied])

  const onCopy = async (e, txHash) => {
    e.preventDefault()
    e.stopPropagation()
    const copied = await doCopy(txHash)

    if (copied) setHasCopied(true)
  }
  //copy to clipboard

  return (
    <PrivatePresaleCSS>
      <Helmet>
        <title>$YOLO Private Presale</title>
      </Helmet>
      <div className='scroll_wrapper'>
        <div className='yolo_token_page_wrapper'>
          <div className='yt_page_left'>
            <h1>YOLOREKT Token Issuance</h1>
            <h2>Private Presale</h2>
            <h3>
              This is a private link and is not available to the public. We have made this available for a select few
              YOLOREKT VIP for very early $YOLO contribution and is by invitation only.
            </h3>
            <h4>
              <Link href={`${NETWORK.blockExplorerUrl}/address/${tokenSaleAddress}`}>Etherscan Contract Link</Link>
            </h4>
            <form className='calculator'>
              <div className='contribution_area'>
                <div className='yolo_balance'>
                  $YOLO balance:{' '}
                  <strong>
                    {Number(yoloTokenPurchasedBalance)
                      ? `${yoloTokenPurchasedBalance} Tokens / ${currencyFormatter(
                          yoloTokenPurchasedBalance * YOLO_TOKEN_USD
                        )} USD`
                      : `${LONG_DASH}/${LONG_DASH}`}
                  </strong>
                </div>
                <div className='title'>Contribute Now</div>

                <div className='connect_to_mm'>
                  <ContentSwitcherByState
                    noWrapper
                    activeState={connectionState}
                    stateObject={{
                      notConnected: (
                        <button className='mm' onClick={onMmConnect}>
                          Connect to MetaMask
                        </button>
                      ),
                      connected: (
                        <>
                          <div className='connected_status'>
                            <img alt='' src={icons.metamask_icon} />
                            Connected with MetaMask
                            <button className='mm_disconnect' onClick={onMmDisconnect}>
                              Disconnect
                            </button>
                          </div>

                          {!isMainnet && (
                            <div className='switch_to_mn'>
                              You need to be connected to {NETWORK.chainName} to contribute
                              <button className='switch_to_mainnet' onClick={onSwitchToNetwork}>
                                Switch to {NETWORK.chainName}
                              </button>
                            </div>
                          )}

                          <div className='mm_details'>
                            <div className='wallet_id_full'>
                              <span>{web3.account}</span>
                            </div>
                            <div className='mm_balance'>
                              Balance{' '}
                              <strong>
                                {etherBalance
                                  ? `${Number(formatEther(etherBalance || 0)).toFixed(6)}... ETH / ${currencyFormatter(
                                      ethBalanceInFiat
                                    )} USD`
                                  : `${LONG_DASH}/${LONG_DASH}`}
                              </strong>
                            </div>
                          </div>
                        </>
                      ),
                    }}
                  />
                </div>
                <div class='details_message'>
                  Please note that your $YOLO will be locked for 6 months for investors in this private presale. There
                  will also be a 10% $YOLO emission per month (for 10 months) after the initial lockup period. There is
                  a $1,000 USD minimum to contribute, so please make sure you have entered the equivalent amount of ETH.
                </div>
                <div className='currency_equivalents'>
                  <div className='current_yolo_price_wrap'>
                    Current price of $YOLO:{' '}
                    <span className='current_yolo_price'>{currencyFormatter(YOLO_TOKEN_USD)}</span>
                  </div>

                  <div className='fields_wrapper'>
                    <div className='amount_wrap eth'>
                      <div className='ind'>
                        <div className='eth_icon'></div>
                        ETH
                      </div>
                      <fieldset>
                        <input
                          type='text'
                          placeholder='0 ETH'
                          className='fiat_amount'
                          value={ethAmount}
                          onChange={onEthChange}
                          maxlength='8'
                        />
                        <input
                          type='text'
                          placeholder='$0.00 USD'
                          className='amount'
                          value={(!!ethFiatAmount && currencyFormatter(ethFiatAmount)) || ''}
                          readonly={true}
                        />
                      </fieldset>
                    </div>
                    <div className='swap'>
                      <div className='swap_icon'></div>
                    </div>
                    <div className='amount_wrap yolo_token'>
                      <div className='ind'>
                        <div className='yolo_token'></div>
                        $YOLO
                      </div>
                      <fieldset>
                        <input
                          type='text'
                          placeholder='0 TOKEN'
                          className='fiat_amount'
                          value={ethFiatAmount / YOLO_TOKEN_USD || ''}
                          maxlength='8'
                          readonly={true}
                        />
                        <input
                          type='text'
                          placeholder='$0.00 USD'
                          value={(!!ethFiatAmount && currencyFormatter(ethFiatAmount)) || ''}
                          className='amount'
                          readonly={true}
                        />
                      </fieldset>
                    </div>
                  </div>
                </div>
              </div>

              <div className='button_container'>
                {/* <div className='details_message'>
                  Please note that your $YOLO will be locked for 6 months for investors in this private presale. There
                  will be a 10% $YOLO emission per month (for 10 months) after the initial lockup period. The emission
                  is continuous over 10 months. There is also a $5,000 USD minimum to contribute, so please make sure
                  you have entered the equivalent amount of ETH.
                </div> */}
                <div className='issue_message'>
                  Your contribution does not meet the {currencyFormatter(MIN_USD_CONTRIBUTION, { decimalDigits: 0 })}{' '}
                  USD minimum
                </div>
                <ContentSwitcherByState
                  noWrapper
                  activeState={tokenPurchaseState.status}
                  stateObject={{
                    PendingSignature: (
                      <button id='' className='waiting' disabled={coontributeButtonDisabled} onClick={buyTokens}>
                        Waiting for MetaMask confirmation
                        <SingleDataLoader loading={true} data={''} />
                      </button>
                    ),
                    Mining: (
                      <button id='' className='waiting' disabled={coontributeButtonDisabled} onClick={buyTokens}>
                        Completing transaction
                        <SingleDataLoader loading={loading} data={''} />
                      </button>
                    ),
                    Success: (
                      <div className='success_message'>
                        <strong>Contribution successful!</strong>
                        <div className='row'>
                          <div className='label'>Transaction hash:</div>
                          <div className='trans_hash'>
                            <Link
                              href={`${NETWORK.blockExplorerUrl}/tx/${tokenPurchaseState.receipt?.transactionHash}`}
                            >
                              {textMiddleTruncate(tokenPurchaseState.receipt?.transactionHash, [15, 15], '...')}
                            </Link>
                            <CopyIcon
                              className='copy_icon'
                              collection='general'
                              name={hasCopied ? 'tick' : 'copy'}
                              masking
                              onClick={(e) => onCopy(e, tokenPurchaseState.receipt?.transactionHash)}
                            />
                          </div>
                        </div>
                        <button class='contribute_again' onClick={onContributeAgain}>
                          Contribute again
                        </button>
                      </div>
                    ),
                    default: (
                      <button id='' className='approve' disabled={coontributeButtonDisabled} onClick={buyTokens}>
                        Contribute using ETH
                      </button>
                    ),
                  }}
                />
                <div className='field_error'>{error}</div>
              </div>
            </form>
          </div>

          <div className='yt_page_right'>
            <div className='yt_logo_full'>
              <img alt='' src={logos.yolo_token_full_logo_2} />
            </div>

            <div className='qanda'>
              <div className='question'>About YOLOREKT</div>
              <div className='answer'>
                <div className='callout_boxes'>
                  <div className='box'>
                    <div className='box_title'>Total Volume</div>
                    <div className='box_value'>+500M</div>
                  </div>
                  <div className='box'>
                    <div className='box_title'>Fees generated</div>
                    <div className='box_value'>+100K</div>
                  </div>
                </div>
                <p>
                  Launched in 2019 as 3-minute prediction platform on ETH, YOLOREKT has evolved into the ultimate
                  super-degen leveraged gaming platform. YOLOREKT designs, builds, and provides advanced unique
                  leveraged gaming experiences that offer killer 1000x leverage using crypto, synthetic stocks, ETFs,
                  and more.
                </p>

                <p>
                  YOLOREKT has a <b>daily average of over 10 Million in volume</b> and{' '}
                  <b>more than 500 Million total volume</b> since its launch, in a matter of months. YOLOREKT’s growth
                  has been consistent and exponential, inching closer to a profitable platform, and month by month
                  already being cash flow positive.
                </p>
                <p>
                  YOLOREKT boasts of gamified innovation on financial products like never before; it's been called the
                  home for brave degens.
                </p>
              </div>
            </div>

            <button className='see_more' onClick={openDocsInNewTab}>
              Learn more about $YOLO
            </button>
          </div>
        </div>

        <footer>
          <div id='landing-details'>
            <div id='priv-terms'>
              <a href='https://docs.yolorekt.finance/docs/legal/privacy-policy' target='blank'>
                Privacy Policy
              </a>
              &nbsp;&nbsp;•&nbsp;&nbsp;
              <a href='https://docs.yolorekt.finance/docs/legal/terms-of-service' target='blank'>
                Terms of Service
              </a>
              &nbsp;&nbsp;•&nbsp;&nbsp;
              <a href='https://docs.yolorekt.finance' target='blank'>
                Docs
              </a>
            </div>

            <div id='copyright'>
              <strong>
                © Copyright{' '}
                <script type='text/javascript'>var year = new Date();document.write(year.getFullYear());</script> 2023
              </strong>
              All rights reserved
            </div>
          </div>

          <div className='about'>
            YOLOREKT designs, builds, and provides advanced next-degen leveraged gaming experiences that offer killer
            incentives, royalties, and leverage up-to 1000x with no minimum portfolio balance.
            <div>
              Visit and play YOLOREKT @{' '}
              <a href='https://yolorekt.finance' target='blank'>
                https://yolorekt.finance
              </a>
            </div>
          </div>

          <div id='landing-social'>
            <a
              href='https://twitter.com/playyolorekt'
              target='blank'
              rel='noopener noreferrer'
              className='icon twitter'
            >
              {''}
            </a>
            <a href='https://vm.tiktok.com/ZTdmEw6xk/' target='blank' rel='noopener noreferrer' className='icon tiktok'>
              {''}
            </a>
            <a href='https://medium.com/yolorekt' target='blank' rel='noopener noreferrer' className='icon medium'>
              {''}
            </a>
            <a
              href='https://t.me/joinchat/NBBv1hQmV26flFM_5MD4_w'
              target='blank'
              rel='noopener noreferrer'
              className='icon telegram'
            >
              {''}
            </a>
            <a href='https://discord.gg/yolorekt' target='blank' rel='noopener noreferrer' className='icon discord'>
              {''}
            </a>
            <a
              href='mailto:support@yolorekt.com?Subject=Hello%20again'
              target='blank'
              rel='noopener noreferrer'
              className='icon email'
            >
              {''}
            </a>
          </div>
        </footer>
      </div>
    </PrivatePresaleCSS>
  )
}
